import React, { useState, useEffect } from 'react';
import { Box, Paper, Snackbar, Alert, CircularProgress } from '@mui/material';
import 'bootstrap/dist/css/bootstrap.min.css';
import Mensual from '../secundarios/Mensual';
import Agenda from '../secundarios/Agenda';
import Notas from '../secundarios/Notas';
import CrearNota from '../secundarios/CrearNota';
import './Principal.css';
import axios from 'axios';
import Disponibles from '../secundarios/Fichar/Disponibles';

const Principal = ({ actualizarDisponibles, setMostrarFichaje, salonSeleccionado }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [totalCitas, setTotalCitas] = useState(0);
  const [notasActualizadas, setNotasActualizadas] = useState(false);
  const [citasDiarias, setCitasDiarias] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Función para actualizar UNA cita específica (optimista)
  const actualizarCita = (idCita, nuevosDatos) => {
    setCitasDiarias((prevCitas) =>
      prevCitas.map((cita) => (cita.id_cita === idCita ? { ...cita, ...nuevosDatos } : cita))
    );
  };

  // Función para recargar TODAS las citas
  const recargarCitas = () => {
    fetchCitas(selectedDate);
  };

  // Función para verificar tickets en lote
  const verificarTickets = async (citas) => {
    try {
      const response = await axios.post(`${apiUrl}/api/tickets/verificar-lote`, {
        ids_citas: citas.map((c) => c.id_cita),
      });
      return response.data; // { id_cita: boolean }
    } catch (error) {
      console.error('Error verificando tickets:', error);
      throw error;
    }
  };

  const fetchCitas = async (date) => {
    try {
      setLoadingTickets(true);
      const adjustedDate = new Date(date);
      adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());
      const fecha = adjustedDate.toISOString().split('T')[0];

      // Aquí agregamos el id_salon a la URL si salonSeleccionado está definido
      const salonParam = salonSeleccionado ? `&id_salon=${salonSeleccionado}` : '';

      // URL con fecha y salón
      const url = `${apiUrl}/api/citas/completas?fecha=${fecha}${salonParam}`;

      // 1. Obtener citas
      const citasResponse = await axios.get(url);
      const citasApi = citasResponse.data; // Estas son las citas únicas

      // 2. Verificar tickets
      const ticketsStatus = await verificarTickets(citasApi);

      // 3. Combinar datos
      const citasTransformadas = citasApi.map((cita) => ({
        ...cita,
        id_cita: cita.id_cita || 0,
        id_cliente: cita.cliente?.id || 0,
        fecha_hora: cita.fecha_hora || `${fecha} 00:00:00`,
        nombre_cliente: cita.cliente?.nombre || 'Sin nombre',
        apellidos_cliente: cita.cliente?.apellidos || '',
        telefono: cita.cliente?.telefono || null,
        tratamientos: cita.tratamientos || [],
        tratamiento: cita.tratamientos?.[0]?.nombre || 'Tratamiento no especificado',
        precio: cita.precio_total?.toString() || '0.00',
        duracion_total: cita.duracion_total || 0,
        id_empleado: cita.id_empleado || 0,
        id_puesto: cita.id_puesto || 0,
        tiene_ticket: ticketsStatus[cita.id_cita] || false,
      }));

      setCitasDiarias(citasTransformadas);

      // Devolver el conteo de citas únicas (desde la API)
      return citasApi.length;
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar los datos',
        severity: 'error',
      });
      setCitasDiarias([]);
      return 0;
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleCitaCreada = () => fetchCitas(selectedDate);

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    const count = await fetchCitas(date);
    setTotalCitas(count);
  };

  useEffect(() => {
    const loadData = async () => {
      const count = await fetchCitas(selectedDate);
      setTotalCitas(count);
    };
    loadData();
  }, [selectedDate, salonSeleccionado]); // también recarga al cambiar de salón

  const handleCitaDeleted = async (idCita) => {
    try {
      await axios.delete(`${apiUrl}/api/delete/${idCita}`);
      setCitasDiarias((prev) => prev.filter((cita) => cita.id_cita !== idCita));
      setSnackbar({
        open: true,
        message: 'Cita eliminada correctamente',
        severity: 'success',
      });
      fetchCitas(selectedDate); // Actualizar datos
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'Error al eliminar la cita',
        severity: 'error',
      });
    }
  };

  return (
    <Box className="contenedor-principal">
      <Box className="row" sx={{ height: '100%' }}>
        <Box
          className="col-md-3"
          sx={{ pr: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Paper elevation={3} className="card-principal">
            <Mensual selectedDate={selectedDate} onDateChange={handleDateChange} />
          </Paper>

          <Paper elevation={3} className="card-principal">
            <Notas
              selectedDate={formatDate(selectedDate)}
              notasActualizadas={notasActualizadas}
              setNotasActualizadas={setNotasActualizadas}
            />
          </Paper>

          <Box className="card-disponibles p-2">
            <Disponibles actualizarDisponibles={actualizarDisponibles} />
          </Box>
        </Box>

        <Box className="col-md-9" sx={{ pl: 1 }}>
          <Paper elevation={3} className="card-principal">
            {loadingTickets ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {console.log('Datos enviados a Agenda:', {
                  fecha: selectedDate,
                  totalCitas: totalCitas,
                  cantidadCitas: citasDiarias.length,
                  muestraCitas: citasDiarias.slice(0, 3), // Muestra las primeras 3 citas
                })}
                <Agenda
                  selectedDate={selectedDate}
                  citas={citasDiarias}
                  setMostrarFichaje={setMostrarFichaje} // ✅ ESTA LÍNEA
                  totalCitas={totalCitas}
                  onCitaCreada={handleCitaCreada}
                  onDateChange={handleDateChange}
                  onCitaDeleted={handleCitaDeleted}
                  setNotasActualizadas={setNotasActualizadas}
                  onActualizarCita={actualizarCita}
                   salonSeleccionado={salonSeleccionado} // ✅ NUEVA PROP
                  onRecargarCitas={recargarCitas}
                />
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// Función auxiliar para formatear fecha
const formatDate = (date) => {
  const adjustedDate = new Date(date);
  adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());
  return adjustedDate.toISOString().split('T')[0];
};

export default Principal;
